/**
 * AppShell — Mantine layout: sol nav + top bar + main content.
 *
 * Tenant-aware: top bar'da tenant adı + brand color, switcher menüsü.
 * RBAC: nav öğeleri RoleGate ile gizli (yetkisiz görmemeli).
 */
import {
  ActionIcon, AppShell as MantineShell, Avatar, Badge, Box, Burger,
  Group, Indicator, Menu, NavLink, ScrollArea, Stack, Text,
  Title, UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications as mantineNotifications } from "@mantine/notifications";
import {
  IconAlertTriangle, IconBell, IconBuilding, IconBuildingWarehouse, IconCash,
  IconCategory, IconChartBar, IconChefHat, IconChevronDown,
  IconClipboardList, IconCreditCard, IconDashboard, IconDiscount,
  IconFileInvoice, IconLogout, IconMail, IconMap, IconPackage, IconReceipt,
  IconReceipt2, IconSettings, IconTruckDelivery, IconUser,
  IconUserCircle, IconUsers, IconUsersGroup,
} from "@tabler/icons-react";
import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../shared/auth";
import { useLineComps, useLineReturns, useResource } from "../../shared/api";
import { useI18n, type Locale } from "../../shared/i18n";
import { apiClient } from "../../shared/api/client";
import { useWebSocket } from "../../shared/ws";
import { Dashboard } from "./pages/Dashboard";
import { ProductsPage } from "./pages/Products";
import { CategoriesPage } from "./pages/Categories";
import { TablesPage } from "./pages/Tables";
import { OrdersPage } from "./pages/Orders";
import { ReservationsPage } from "./pages/Reservations";
import { FloorPlanLivePage } from "./pages/FloorPlanLive";
import { StockItemsPage } from "./pages/StockItems";
import { StockLocationsPage } from "./pages/StockLocations";
import { StockAlertsPage } from "./pages/StockAlerts";
import { DailyCashierPage } from "./pages/DailyCashier";
import { MonthlyPLPage } from "./pages/MonthlyPL";
import { ReorderSuggestionsPage } from "./pages/ReorderSuggestions";
import { ProductSalesPage } from "./pages/ProductSales";
import { SettingsPage } from "./pages/Settings";
import { CustomersPage } from "./pages/Customers";
import { LoyaltyPage } from "./pages/Loyalty";
import { ProcurementDashboardPage } from "./pages/ProcurementDashboard";
import { SuppliersPage } from "./pages/Suppliers";
import { ProcurementCategoriesPage } from "./pages/ProcurementCategories";
import { PurchaseOrdersPage } from "./pages/PurchaseOrders";
import { RFQPage } from "./pages/RFQ";
import { GoodsReceiptsPage } from "./pages/GoodsReceipts";
import { SupplierInvoicesPage } from "./pages/SupplierInvoices";
import { ExpensesPage } from "./pages/Expenses";
import { InvoicesPage } from "./pages/Invoices";
import { DeliveryPage } from "./pages/Delivery";
import { NotificationsPage } from "./pages/Notifications";
import { CampaignsPage } from "./pages/Campaigns";
import { PersonnelPage } from "./pages/Personnel";
import { UsersPage } from "./pages/Users";
import { AuditLogPage } from "./pages/AuditLog";
import { BillingPage } from "./pages/Billing";
import { RecipePage } from "./pages/Recipe";
import { ModifiersPage } from "./pages/Modifiers";
import { FloorPlanEditorPage } from "./pages/FloorPlanEditor";
import { PaymentsPage } from "./pages/Payments";
import { CashMovementsPage } from "./pages/CashMovements";
import { DiscountsPage } from "./pages/Discounts";
import { BranchesPage } from "./pages/Branches";
import { ImportExportPage } from "./pages/ImportExport";
import { StockOperationsPage } from "./pages/StockOperations";
import { AdjustmentApprovalsPage } from "./pages/AdjustmentApprovals";
import { LineCompsPage } from "./pages/LineComps";
import { LineReturnsPage } from "./pages/LineReturns";
import { ExternalMappingPage } from "./pages/ExternalMapping";
import { SystemSettingsPage } from "./pages/SystemSettings";
import { KdsStationsPage } from "./pages/KdsStations";
import { DevicesPage } from "./pages/Devices";
import { ProviderCredentialsPage } from "./pages/ProviderCredentials";
import { FiscalReceiptsPage } from "./pages/FiscalReceipts";

type PageKey =
  | "dashboard" | "products" | "categories" | "modifiers" | "recipe"
  | "tables" | "floor-plan" | "floor-editor"
  | "orders" | "reservations" | "stock-items" | "stock-locations" | "stock-alerts"
  | "daily-cashier" | "monthly-pl" | "reorder-suggestions" | "product-sales" | "settings"
  | "customers" | "campaigns" | "loyalty"
  | "procurement-dashboard" | "suppliers" | "procurement-categories" | "rfq" | "po" | "gr" | "supplier-invoices"
  | "expenses" | "invoices" | "delivery" | "notifications" | "payments"
  | "personnel" | "users" | "audit" | "billing"
  | "cash-movements" | "discounts" | "branches"
  | "import-export" | "stock-ops" | "adjustment-approvals" | "line-comps" | "line-returns" | "external-mapping" | "system-settings"
  | "kds-stations" | "devices" | "provider-credentials" | "fiscal-receipts";

interface NavItem {
  key: PageKey;
  label: string;
  icon: React.ComponentType<any>;
  group: string;
  // Görünürlük yetki kontrolü (Faz 4). `permission` verilirse menü öğesi
  // sadece kullanıcının o resource üzerinde verilen action'a (genelde READ)
  // izni varsa görünür — owner İzin Matrisi'nden hücreyi kapatınca öğe
  // otomatik gizlenir. `roles` geriye-uyum/permission'a haritalanmayan
  // öğeler (ör. OWNER-only sistem ekranları) için fallback olarak kalır.
  // Bir öğede ikisi de yoksa herkese görünür (ör. Dashboard).
  permission?: { resource: string; action: string };
  roles?: string[];
}

// READ varsayılan action — menü "görüntüleyebilir mi?" sorusudur.
const R = (resource: string): NonNullable<NavItem["permission"]> => ({ resource, action: "READ" });

const NAV_ITEMS: NavItem[] = [
  { key: "dashboard", label: "Dashboard", icon: IconDashboard, group: "" },

  { key: "orders", label: "Siparişler", icon: IconClipboardList, group: "POS", permission: R("ORDER") },
  { key: "line-comps", label: "🎁 İkram Onayları", icon: IconClipboardList, group: "POS", roles: ["OWNER", "MANAGER"] },
  { key: "line-returns", label: "🔄 İade Onayları", icon: IconClipboardList, group: "POS", roles: ["OWNER", "MANAGER"] },
  { key: "tables", label: "Masalar", icon: IconBuilding, group: "POS", permission: R("TABLE") },
  { key: "floor-plan", label: "Salon Planı (Canlı)", icon: IconMap, group: "POS", permission: R("TABLE") },
  { key: "floor-editor", label: "Plan Editörü", icon: IconMap, group: "POS", roles: ["OWNER", "MANAGER"] },
  { key: "reservations", label: "Rezervasyonlar", icon: IconCategory, group: "POS", permission: R("RESERVATION") },

  { key: "categories", label: "Kategoriler", icon: IconCategory, group: "Menü", permission: R("CATEGORY") },
  { key: "products", label: "Ürünler", icon: IconPackage, group: "Menü", permission: R("PRODUCT") },
  { key: "modifiers", label: "Modifier'lar", icon: IconDiscount, group: "Menü", permission: R("MODIFIER") },
  { key: "recipe", label: "Reçete (BOM)", icon: IconPackage, group: "Menü", permission: R("RECIPE") },
  { key: "kds-stations", label: "Mutfak İstasyonları", icon: IconChefHat, group: "Menü", roles: ["OWNER", "MANAGER"] },

  { key: "stock-locations", label: "Depolar", icon: IconBuildingWarehouse, group: "Stok", roles: ["OWNER", "MANAGER"] },
  { key: "stock-items", label: "Stok", icon: IconPackage, group: "Stok", permission: R("STOCK") },
  { key: "stock-alerts", label: "Kritik Stok", icon: IconAlertTriangle, group: "Stok", permission: R("STOCK") },
  { key: "stock-ops", label: "Operasyonlar (Transfer/Fire)", icon: IconPackage, group: "Stok", roles: ["OWNER", "MANAGER", "CHEF"] },
  { key: "adjustment-approvals", label: "🔐 Düzeltme Onayları", icon: IconAlertTriangle, group: "Stok", roles: ["OWNER", "MANAGER"] },

  { key: "daily-cashier", label: "Z Raporu (Gün)", icon: IconReceipt, group: "Raporlar", permission: R("REPORT") },
  { key: "monthly-pl", label: "Aylık Kâr/Zarar", icon: IconChartBar, group: "Raporlar", permission: R("REPORT") },
  { key: "product-sales", label: "Ürün Satış", icon: IconChartBar, group: "Raporlar", permission: R("REPORT") },
  { key: "reorder-suggestions", label: "🛒 Sipariş Önerisi", icon: IconChartBar, group: "Raporlar", roles: ["OWNER", "MANAGER"] },

  { key: "customers", label: "Müşteriler", icon: IconUsersGroup, group: "CRM", permission: R("CUSTOMER_PII") },
  { key: "campaigns", label: "Toplu Mail", icon: IconMail, group: "CRM", roles: ["OWNER", "MANAGER"] },
  { key: "loyalty", label: "Sadakat", icon: IconUsers, group: "CRM", permission: R("LOYALTY") },

  { key: "personnel", label: "Personel", icon: IconUser, group: "İK", permission: R("WAITER_PROFILE") },

  { key: "procurement-dashboard", label: "📊 Satın Alma Özeti", icon: IconChartBar, group: "Satın Alma", permission: R("PROCUREMENT") },
  { key: "suppliers", label: "Tedarikçiler", icon: IconTruckDelivery, group: "Satın Alma", permission: R("PROCUREMENT") },
  { key: "procurement-categories", label: "Tedarik Kategorileri", icon: IconCategory, group: "Satın Alma", permission: R("PROCUREMENT") },
  { key: "rfq", label: "🛒 Teklif İste (RFQ)", icon: IconClipboardList, group: "Satın Alma", permission: R("PROCUREMENT") },
  { key: "po", label: "Satın Alma Siparişleri", icon: IconClipboardList, group: "Satın Alma", permission: R("PROCUREMENT") },
  { key: "gr", label: "Mal Girişi (İrsaliye)", icon: IconReceipt2, group: "Satın Alma", permission: R("PROCUREMENT") },
  { key: "supplier-invoices", label: "Tedarikçi Faturaları", icon: IconFileInvoice, group: "Satın Alma", permission: R("PROCUREMENT") },

  // FINANS — kullanıcı akışına göre sıralı: para nasıl girdi → kasada ne var
  // → ne harcadım/kârım → indirim → resmi/fatura → kontür.
  { key: "payments", label: "Ödemeler & İadeler", icon: IconCreditCard, group: "Finans", permission: R("PAYMENT") },
  { key: "cash-movements", label: "Kasa Hareketleri", icon: IconCash, group: "Finans", roles: ["OWNER", "MANAGER", "CASHIER"] },
  { key: "expenses", label: "Gelir-Gider", icon: IconChartBar, group: "Finans", permission: R("EXPENSE") },
  { key: "discounts", label: "İndirimler", icon: IconDiscount, group: "Finans", roles: ["OWNER", "MANAGER"] },
  { key: "invoices", label: "E-Fatura", icon: IconFileInvoice, group: "Finans", roles: ["OWNER", "ACCOUNTANT"] },
  { key: "fiscal-receipts", label: "🇹🇷 Mali Fişler (YN ÖKC)", icon: IconReceipt, group: "Finans", permission: R("REPORT") },
  { key: "billing", label: "Kontür / Faturalama", icon: IconCreditCard, group: "Finans", permission: R("BILLING") },

  { key: "delivery", label: "Teslimat", icon: IconTruckDelivery, group: "Operasyon", permission: R("ORDER") },
  { key: "notifications", label: "Bildirimler", icon: IconBell, group: "Operasyon" },
  { key: "external-mapping", label: "Platform Eşleme", icon: IconTruckDelivery, group: "Operasyon", roles: ["OWNER", "MANAGER"] },
  { key: "import-export", label: "Toplu Veri (Excel)", icon: IconPackage, group: "Operasyon", roles: ["OWNER", "MANAGER", "ACCOUNTANT"] },
  { key: "devices", label: "POS Donanımları", icon: IconCreditCard, group: "Operasyon", roles: ["OWNER", "MANAGER"] },
  { key: "provider-credentials", label: "Entegrasyon Anahtarları", icon: IconSettings, group: "Operasyon", roles: ["OWNER"] },

  { key: "branches", label: "Şubeler", icon: IconBuilding, group: "Sistem", roles: ["OWNER"] },
  { key: "users", label: "Kullanıcılar & RBAC", icon: IconUsers, group: "Sistem", permission: R("USER") },
  { key: "audit", label: "Audit Log", icon: IconChartBar, group: "Sistem", permission: R("AUDIT_LOG") },
  { key: "system-settings", label: "Sistem Ayarları (KV)", icon: IconSettings, group: "Sistem", roles: ["OWNER"] },
  { key: "settings", label: "Tenant Profili", icon: IconSettings, group: "Sistem", permission: R("SETTINGS") },
];

function useNotificationCenter() {
  // Bell badge: WS push (/ws/notifications/) primary, polling sadece WS
  // koptuğunda fallback (60 sn — önceden her sekme 15 sn'de bir istek
  // atıyordu, admin oturumu boyunca saatte ~240 gereksiz istek). Mesaj
  // gelince badge + Notifications sayfası invalidate + toast.
  const qc = useQueryClient();
  const { lastMessage, status: wsStatus } = useWebSocket<any>("/ws/notifications/");
  const wsOk = wsStatus === "OPEN";
  const { data, refetch } = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: async () => {
      const { data } = await apiClient.get(
        "/api/v1/notifications/unread-count/",
        { params: { mine: 1 } },
      );
      return data as { count: number };
    },
    refetchInterval: wsOk ? false : 60000,
  });
  useEffect(() => {
    if (!lastMessage) return;
    // notification.message tipi: { id, title, body, severity, event_name }
    const sev = String(lastMessage.severity || "INFO");
    const color = sev === "CRITICAL" ? "red" : sev === "WARNING" ? "yellow" : "blue";
    mantineNotifications.show({
      title: lastMessage.title || "Bildirim",
      message: lastMessage.body || "",
      color,
      autoClose: sev === "CRITICAL" ? false : 6000,
    });
    qc.invalidateQueries({ queryKey: ["notifications"] });
    refetch();
  }, [lastMessage, qc, refetch]);

  return data?.count ?? 0;
}

// URL hash'inden geçerli sayfa anahtarını oku — yenileme/derin-link/geri-ileri
// sonrası aynı sayfaya dönülsün. Bilinmeyen/boş hash → dashboard.
function pageFromHash(): PageKey {
  // Yalnız İLK segmenti al — alt-rotalar (#rfq/<id> gibi derin-linkler) sayfa
  // anahtarını bozmasın. "rfq/e3c..." → "rfq". Aksi halde geçersiz PageKey
  // hiçbir sayfaya eşleşmez ve yenilemede beyaz ekran olur.
  const h = window.location.hash.replace(/^#\/?/, "").trim();
  const page = h.split(/[/?#]/)[0];
  return (page || "dashboard") as PageKey;
}

export function AppShell() {
  // mobileOpened: dar ekranda navbar açık mı (burger). desktopOpened: geniş
  // ekranda navbar açık mı (collapse toggle). İkisi ayrı: mobilde default
  // kapalı, masaüstünde default açık.
  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true);
  // İlk değer URL hash'inden — sayfa yenilense de aynı ekran açılır.
  const [active, setActive] = useState<PageKey>(() => pageFromHash());
  const { user, tenant, role, logout, memberships, permissions, menuHidden } = useAuth();
  const { lang, setLang } = useI18n();
  const unreadCount = useNotificationCenter();

  // Menü rozetleri — bekleyen ikram/iade onay sayısı (POS/QR/Hazır'daki gibi).
  // Yalnız bu ekranları gören rol için anlamlı; backend zaten RBAC uygular.
  const canSeeApprovals = role === "OWNER" || role === "MANAGER";
  const { data: pendingComps } = useLineComps(
    canSeeApprovals ? { status: "PENDING" } : undefined,
  );
  const { data: pendingReturns } = useLineReturns(
    canSeeApprovals ? { status: "PENDING" } : undefined,
  );
  // Satın alma bekleyen rozetleri — onay bekleyen mal girişi + işlenmeyi
  // bekleyen fatura. PROCUREMENT.READ olmayan rol için sorgu atılmaz.
  const canProc = !!permissions.find(
    (p) => p.resource === "PROCUREMENT" && p.actions.includes("READ"),
  );
  const { data: pendingGr } = useResource<any>("goods-receipts", {
    filters: { status: "DRAFT" }, enabled: canProc, refetchInterval: 60_000,
  });
  const { data: pendingInv } = useResource<any>("supplier-invoices", {
    filters: { status: "DRAFT" }, enabled: canProc, refetchInterval: 60_000,
  });
  const countOf = (d: any) =>
    (d?.count ?? (Array.isArray(d?.results) ? d.results.length : Array.isArray(d) ? d.length : 0)) || 0;
  // key → bekleyen sayısı. NavLink rightSection'da rozet olarak gösterilir.
  const navBadges: Partial<Record<PageKey, number>> = {
    "line-comps": canSeeApprovals ? countOf(pendingComps) : 0,
    "line-returns": canSeeApprovals ? countOf(pendingReturns) : 0,
    "gr": canProc ? countOf(pendingGr) : 0,
    "supplier-invoices": canProc ? countOf(pendingInv) : 0,
  };

  // Sayfa geçişi: state'i değiştir + URL hash'ini güncelle (history'ye yaz).
  // Bu sayede tarayıcı Geri/İleri tuşları admin içinde çalışır. Aynı sayfaya
  // tekrar gitmek yeni history kaydı oluşturmaz (replace yerine no-op).
  const goTo = useCallback((page: PageKey) => {
    setActive(page);
    if (pageFromHash() !== page) {
      window.history.pushState(null, "", `#${page}`);
    }
  }, []);

  // Tarayıcı Geri/İleri → hash değişir → popstate → aktif sayfayı senkronla.
  useEffect(() => {
    const onPop = () => setActive(pageFromHash());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // İlk açılışta hash boşsa kanonik #dashboard yaz (geri tuşu tutarlılığı).
  useEffect(() => {
    if (!window.location.hash) {
      window.history.replaceState(null, "", `#${active}`);
    }
    // sadece mount'ta
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sayfa içi linklerin AppShell navigasyonunu tetikleyebilmesi için
  // global event dinleyici. Hedef sayfaya parametre geçirmek için
  // sessionStorage'a "admin:nav:<page>" key'iyle JSON yazılır; hedef sayfa
  // mount olunca okur ve siler (tek seferlik). Kullanım:
  //   window.dispatchEvent(new CustomEvent("admin:navigate", {
  //     detail: { page: "stock-items", params: { search: "Kola" } }
  //   }))
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail?.page) return;
      if (detail.params) {
        try {
          sessionStorage.setItem(
            `admin:nav:${detail.page}`,
            JSON.stringify(detail.params),
          );
        } catch { /* ignore quota errors */ }
      }
      goTo(detail.page as PageKey);
    };
    window.addEventListener("admin:navigate", handler);
    return () => window.removeEventListener("admin:navigate", handler);
  }, [goTo]);

  // Bir nav öğesi görünür mü? (Faz 4 — rol-bazlı menü görünürlüğü)
  // İKİ bağımsız kapı; öğe ikisini de geçmeli:
  //   1) YETKİ: permission verilmişse kullanıcının o resource.action izni
  //      olmalı (owner İzin Matrisi'nden kapatınca otomatik gizlenir).
  //      permission yoksa roles fallback'i; o da yoksa herkese açık.
  //   2) GÖRÜNÜRLÜK: owner bu öğeyi rol için gizlemiş olmamalı
  //      (Menü Görünürlüğü ekranı → menuHidden). Yetki ile bağımsızdır:
  //      yetkisi olsa bile owner menüden saklayabilir.
  const canSee = (item: NavItem): boolean => {
    // 2) Görünürlük override'ı — yetkiden önce, ucuz kontrol.
    if (menuHidden.includes(item.key)) return false;

    // 1) Yetki kapısı.
    if (item.permission) {
      const found = permissions.find((p) => p.resource === item.permission!.resource);
      return !!found && found.actions.includes(item.permission.action);
    }
    if (item.roles) {
      return !!role && item.roles.includes(role);
    }
    return true;
  };

  // Grup başlıklarına göre görünür nav öğelerini topla
  const grouped: Record<string, NavItem[]> = {};
  NAV_ITEMS.forEach((item) => {
    if (!canSee(item)) return;
    grouped[item.group] = grouped[item.group] || [];
    grouped[item.group].push(item);
  });

  return (
    <MantineShell
      header={{ height: 64 }}
      navbar={{
        width: 260,
        breakpoint: "sm",
        collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
      }}
      padding="md"
    >
      <MantineShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group wrap="nowrap" style={{ minWidth: 0 }}>
            {/* Dar ekran: mobil aç/kapa. Geniş ekran: collapse toggle. */}
            <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" />
            <Burger opened={desktopOpened} onClick={toggleDesktop} visibleFrom="sm" size="sm" />
            <Title order={3} c="brand" lineClamp={1} style={{ minWidth: 0 }}>
              🍽️ {tenant?.name || "Restoran"}
            </Title>
            {role && (
              <Badge variant="light" color="brand" visibleFrom="xs">{role}</Badge>
            )}
          </Group>

          <Group wrap="nowrap" gap="xs">
            <Menu shadow="md" width={120}>
              <Menu.Target>
                <ActionIcon variant="subtle" size="lg" aria-label="Dil">
                  <Text size="sm" fw={700}>{lang.toUpperCase()}</Text>
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {(["tr", "en", "ar", "ru"] as Locale[]).map((l) => (
                  <Menu.Item key={l} onClick={() => setLang(l)} disabled={l === lang}>
                    {l.toUpperCase()} {l === lang && "✓"}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>

            <Indicator
              inline
              label={unreadCount > 99 ? "99+" : unreadCount}
              size={16}
              disabled={unreadCount === 0}
              color="red"
            >
              <ActionIcon
                variant="subtle"
                size="lg"
                aria-label="Bildirimler"
                onClick={() => goTo("notifications")}
              >
                <IconBell size={20} />
              </ActionIcon>
            </Indicator>

            <Menu shadow="md" width={240}>
              <Menu.Target>
                <UnstyledButton>
                  <Group gap="xs" wrap="nowrap">
                    <Avatar color="brand" radius="xl">
                      {user?.username?.[0]?.toUpperCase()}
                    </Avatar>
                    <Box visibleFrom="sm" style={{ minWidth: 0, maxWidth: 160 }}>
                      <Text size="sm" fw={500} lineClamp={1}>{user?.username}</Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>{user?.email}</Text>
                    </Box>
                    <IconChevronDown size={14} />
                  </Group>
                </UnstyledButton>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>Hesap</Menu.Label>
                <Menu.Item leftSection={<IconUserCircle size={14} />}>
                  Profilim
                </Menu.Item>
                {memberships.length > 1 && (
                  <>
                    <Menu.Label>Tenant Değiştir</Menu.Label>
                    {memberships.map((m) => (
                      <Menu.Item key={m.tenant_id} disabled={m.tenant_id === tenant?.id}>
                        {m.tenant_name} {m.tenant_id === tenant?.id && "(aktif)"}
                      </Menu.Item>
                    ))}
                  </>
                )}
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconLogout size={14} />}
                  color="red"
                  onClick={logout}
                >
                  Çıkış
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </MantineShell.Header>

      <MantineShell.Navbar p="md">
        <ScrollArea>
          {Object.entries(grouped).map(([group, items]) => (
            <Stack key={group} gap={4} mb="md">
              {group && (
                <Text size="xs" c="dimmed" fw={700} px="sm" mt="sm">
                  {group.toUpperCase()}
                </Text>
              )}
              {items.map((item) => {
                const Icon = item.icon;
                const badgeCount = navBadges[item.key] || 0;
                return (
                  <NavLink
                    key={item.key}
                    label={item.label}
                    leftSection={<Icon size={18} />}
                    rightSection={
                      badgeCount > 0 ? (
                        <Badge size="sm" circle color="orange" variant="filled">
                          {badgeCount > 99 ? "99+" : badgeCount}
                        </Badge>
                      ) : undefined
                    }
                    active={active === item.key}
                    onClick={() => {
                      goTo(item.key);
                      // Yalnız mobilde nav seçince paneli kapat; masaüstünde
                      // collapse tercihine dokunma.
                      if (mobileOpened) toggleMobile();
                    }}
                  />
                );
              })}
            </Stack>
          ))}
        </ScrollArea>
      </MantineShell.Navbar>

      {/* Main: viewport'a sabit yükseklik + overflow:hidden. İçteki wrapper
          tek dikey kaydırma alanı: normal sayfalar burada kayar; h="100%" ile
          kendini dolduran sayfalar (Products) ise içlerinde kayar, wrapper
          kaymaz → asla çift scrollbar olmaz. */}
      <MantineShell.Main
        style={{
          height: "calc(100vh - 64px)",  // header yüksekliği (header={{height:64}})
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
        {active === "dashboard" && <Dashboard />}
        {active === "products" && <ProductsPage />}
        {active === "categories" && <CategoriesPage />}
        {active === "tables" && <TablesPage />}
        {active === "orders" && <OrdersPage />}
        {active === "reservations" && <ReservationsPage />}
        {active === "floor-plan" && <FloorPlanLivePage />}
        {active === "stock-items" && <StockItemsPage />}
        {active === "stock-locations" && <StockLocationsPage />}
        {active === "stock-alerts" && <StockAlertsPage />}
        {active === "daily-cashier" && <DailyCashierPage />}
        {active === "monthly-pl" && <MonthlyPLPage />}
        {active === "reorder-suggestions" && <ReorderSuggestionsPage />}
        {active === "product-sales" && <ProductSalesPage />}
        {active === "customers" && <CustomersPage />}
        {active === "campaigns" && <CampaignsPage />}
        {active === "loyalty" && <LoyaltyPage />}
        {active === "procurement-dashboard" && <ProcurementDashboardPage />}
        {active === "suppliers" && <SuppliersPage />}
        {active === "procurement-categories" && <ProcurementCategoriesPage />}
        {active === "rfq" && <RFQPage />}
        {active === "po" && <PurchaseOrdersPage />}
        {active === "gr" && <GoodsReceiptsPage />}
        {active === "supplier-invoices" && <SupplierInvoicesPage />}
        {active === "expenses" && <ExpensesPage />}
        {active === "invoices" && <InvoicesPage />}
        {active === "delivery" && <DeliveryPage />}
        {active === "notifications" && <NotificationsPage />}
        {active === "personnel" && <PersonnelPage />}
        {active === "users" && <UsersPage />}
        {active === "audit" && <AuditLogPage />}
        {active === "billing" && <BillingPage />}
        {active === "modifiers" && <ModifiersPage />}
        {active === "recipe" && <RecipePage />}
        {active === "floor-editor" && <FloorPlanEditorPage />}
        {active === "payments" && <PaymentsPage />}
        {active === "cash-movements" && <CashMovementsPage />}
        {active === "discounts" && <DiscountsPage />}
        {active === "branches" && <BranchesPage />}
        {active === "import-export" && <ImportExportPage />}
        {active === "stock-ops" && <StockOperationsPage />}
        {active === "adjustment-approvals" && <AdjustmentApprovalsPage />}
        {active === "line-comps" && <LineCompsPage />}
        {active === "line-returns" && <LineReturnsPage />}
        {active === "external-mapping" && <ExternalMappingPage />}
        {active === "system-settings" && <SystemSettingsPage />}
        {active === "kds-stations" && <KdsStationsPage />}
        {active === "devices" && <DevicesPage />}
        {active === "provider-credentials" && <ProviderCredentialsPage />}
        {active === "fiscal-receipts" && <FiscalReceiptsPage />}
        {active === "settings" && <SettingsPage />}
        </div>
      </MantineShell.Main>
    </MantineShell>
  );
}
